import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users, Plus, Edit2, Trash2, Loader2, Tag, Palette, RefreshCw
} from 'lucide-react';
import api from '../api';

const SEGMENT_COLORS = [
  { value: 'bg-blue-100 text-blue-700', label: 'Blue', preview: 'bg-blue-100' },
  { value: 'bg-emerald-100 text-emerald-700', label: 'Emerald', preview: 'bg-emerald-100' },
  { value: 'bg-purple-100 text-purple-700', label: 'Purple', preview: 'bg-purple-100' },
  { value: 'bg-cyan-100 text-cyan-700', label: 'Cyan', preview: 'bg-cyan-100' },
  { value: 'bg-pink-100 text-pink-700', label: 'Pink', preview: 'bg-pink-100' },
  { value: 'bg-amber-100 text-amber-700', label: 'Amber', preview: 'bg-amber-100' },
  { value: 'bg-orange-100 text-orange-700', label: 'Orange', preview: 'bg-orange-100' },
  { value: 'bg-red-100 text-red-700', label: 'Red', preview: 'bg-red-100' },
  { value: 'bg-green-100 text-green-700', label: 'Green', preview: 'bg-green-100' },
  { value: 'bg-indigo-100 text-indigo-700', label: 'Indigo', preview: 'bg-indigo-100' },
  { value: 'bg-slate-100 text-slate-700', label: 'Slate', preview: 'bg-slate-100' },
  { value: 'bg-rose-100 text-rose-700', label: 'Rose', preview: 'bg-rose-100' }
];

export const CustomerSegmentsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [segments, setSegments] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [deletingSegment, setDeletingSegment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [form, setForm] = useState({
    value: '',
    label: '',
    description: '',
    color: 'bg-blue-100 text-blue-700',
    is_active: true
  });

  useEffect(() => {
    fetchSegments();
  }, [includeInactive]);

  const fetchSegments = async () => {
    try {
      setRefreshing(true);
      const res = await api.get('/segments', { params: { include_inactive: includeInactive } });
      setSegments(res.data.segments || []);
    } catch (error) {
      console.error('Failed to fetch segments:', error);
      toast.error('Failed to load segments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleCreate = () => {
    setEditingSegment(null);
    setForm({
      value: '',
      label: '',
      description: '',
      color: 'bg-blue-100 text-blue-700',
      is_active: true
    });
    setShowDialog(true);
  };

  const handleEdit = (segment) => {
    setEditingSegment(segment);
    setForm({
      value: segment.value,
      label: segment.label,
      description: segment.description || '',
      color: segment.color || 'bg-blue-100 text-blue-700',
      is_active: segment.is_active !== false
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.label) {
      toast.error('Segment label is required');
      return;
    }
    if (!form.value) {
      // Auto-generate value from label
      form.value = form.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    }

    setSaving(true);
    try {
      if (editingSegment) {
        await api.put(`/segments/${editingSegment.id}`, form);
        toast.success('Segment updated');
      } else {
        await api.post('/segments', form);
        toast.success('Segment created');
      }
      setShowDialog(false);
      fetchSegments();
    } catch (error) {
      console.error('Failed to save segment:', error);
      toast.error(error.response?.data?.detail || 'Failed to save segment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingSegment) return;

    try {
      await api.delete(`/segments/${deletingSegment.id}`);
      toast.success('Segment deleted');
      setShowDeleteDialog(false);
      setDeletingSegment(null);
      fetchSegments();
    } catch (error) {
      console.error('Failed to delete segment:', error);
      toast.error('Failed to delete segment');
    }
  };

  const handleToggleActive = async (segment) => {
    try {
      await api.put(`/segments/${segment.id}`, { is_active: !segment.is_active });
      toast.success(segment.is_active ? 'Segment deactivated' : 'Segment activated');
      fetchSegments();
    } catch (error) {
      console.error('Failed to toggle segment:', error);
      toast.error('Failed to update segment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-segments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tag className="h-6 w-6" />
            Customer Segments
          </h1>
          <p className="text-muted-foreground">
            Manage customer segments for targeting and categorization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={fetchSegments}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button onClick={handleCreate} data-testid="create-segment-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Segment
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Tag className="h-6 w-6 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{segments.length}</p>
                <p className="text-sm text-muted-foreground">Total Segments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {segments.filter(s => s.is_active !== false).length}
                </p>
                <p className="text-sm text-muted-foreground">Active Segments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label htmlFor="include-inactive" className="text-sm">Show Inactive</Label>
              </div>
              <Switch
                id="include-inactive"
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Segments Grid */}
      <Card>
        <CardHeader>
          <CardTitle>All Segments</CardTitle>
          <CardDescription>Click on a segment to edit it</CardDescription>
        </CardHeader>
        <CardContent>
          {segments.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <Tag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No segments found</h3>
              <p className="text-muted-foreground mb-4">
                Create your first customer segment to start categorizing customers
              </p>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Segment
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment) => (
                <div
                  key={segment.id}
                  className={`p-4 border rounded-lg transition-all hover:shadow-md ${
                    segment.is_active === false ? 'opacity-60 bg-slate-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <Badge className={segment.color || 'bg-slate-100 text-slate-700'}>
                      {segment.label}
                    </Badge>
                    <div className="flex items-center gap-1">
                      {hasRole(['admin', 'marketing_manager']) && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(segment)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setDeletingSegment(segment);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Value: <code className="bg-slate-100 px-1 rounded">{segment.value}</code>
                  </p>
                  {segment.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {segment.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <span className="text-xs text-muted-foreground">
                      {segment.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                    {hasRole(['admin', 'marketing_manager']) && (
                      <Switch
                        checked={segment.is_active !== false}
                        onCheckedChange={() => handleToggleActive(segment)}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSegment ? 'Edit Segment' : 'Create Segment'}
            </DialogTitle>
            <DialogDescription>
              {editingSegment 
                ? 'Update the segment details' 
                : 'Create a new customer segment for targeting'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="label">Segment Label *</Label>
              <Input
                id="label"
                placeholder="e.g., Enterprise, Small Business, Premium"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="value">Segment Value (ID)</Label>
              <Input
                id="value"
                placeholder="Auto-generated from label if empty"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                disabled={!!editingSegment}
              />
              <p className="text-xs text-muted-foreground">
                Used as internal identifier. Cannot be changed after creation.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Optional description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <Select
                value={form.color}
                onValueChange={(v) => setForm({ ...form, color: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEGMENT_COLORS.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${color.preview}`} />
                        {color.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-2">
                <p className="text-xs text-muted-foreground mb-1">Preview:</p>
                <Badge className={form.color}>{form.label || 'Segment Name'}</Badge>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">
                  Inactive segments won't appear in dropdowns
                </p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingSegment ? 'Save Changes' : 'Create Segment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Segment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingSegment?.label}&quot;? 
              This action cannot be undone. Contacts assigned to this segment will 
              no longer have this segment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerSegmentsPage;
